import { useEffect } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import { PROJECTS } from "@/data/content";
import { useSeo } from "@/lib/seo";

export const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const index = PROJECTS.findIndex((p) => p.slug === slug);
  const p = PROJECTS[index];

  useSeo(
    p
      ? { title: `${p.client} | Progetti — Starting Point Agency`, description: p.punto.slice(0, 155) }
      : { title: "Progetti | Starting Point Agency" }
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!p) return <Navigate to="/progetti" replace />;

  return (
    <div data-testid="project-detail" className="bg-white">
      <section className="bg-navy bg-grid-dark pb-16 pt-28 text-white lg:pt-36">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Link to="/progetti" data-testid="project-back-link" className="group inline-flex items-center gap-2 font-display text-xs font-bold tracking-[0.18em] text-white/60 transition-colors hover:text-brand-cyan">
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" /> TUTTI I PROGETTI
          </Link>

          <div className="mt-10 grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <Kicker dark>{`Progetto 0${index + 1} — ${p.sector}`}</Kicker>
                <h1 className="mt-5 font-display text-5xl font-black leading-[0.95] tracking-tight sm:text-7xl">
                  {p.client.split(" ").slice(0, -1).join(" ")} <span className="text-brand-gradient">{p.client.split(" ").slice(-1)}</span>
                </h1>
                <p className="mt-4 font-mono text-[11px] tracking-[0.3em] text-white/40">{p.location}</p>
                <p className="mt-8 max-w-xl text-lg leading-relaxed text-white/70">{p.punto}</p>
                <div className="mt-9 flex flex-wrap gap-3">
                  {p.links.map((l) => (
                    <a
                      key={l.label}
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`project-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-white transition-colors hover:border-brand-cyan hover:text-brand-cyan"
                    >
                      {l.label.toUpperCase()} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ))}
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="relative mx-auto aspect-[9/16] max-h-[70vh] w-full max-w-sm overflow-hidden rounded-2xl border-[5px] border-white/10 shadow-[0_40px_90px_-40px_rgba(0,0,0,0.8)]">
                  <video
                    key={p.slug}
                    className="h-full w-full object-cover"
                    src={p.video}
                    poster={p.poster}
                    muted
                    loop
                    playsInline
                    autoPlay
                    controls
                    aria-label={`Video hero del progetto ${p.client}`}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="space-y-12 lg:col-span-7">
              <Reveal>
                <div>
                  <h2 className="font-mono text-[11px] tracking-[0.3em] text-brand-blue">L'OBIETTIVO</h2>
                  <p className="mt-3 text-lg leading-relaxed text-navy">{p.obiettivo}</p>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <div>
                  <h2 className="font-mono text-[11px] tracking-[0.3em] text-brand-blue">LA STRATEGIA</h2>
                  <p className="mt-3 text-base leading-relaxed text-slate-600">{p.strategia}</p>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div>
                  <h2 className="font-mono text-[11px] tracking-[0.3em] text-brand-blue">LE ATTIVITÀ REALIZZATE</h2>
                  <ul className="mt-4 flex flex-wrap gap-2">
                    {p.attivita.map((a) => (
                      <li key={a} className="rounded-full border border-slate-200 bg-brand-light px-4 py-1.5 text-xs font-semibold text-navy">{a}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              {p.gallery.length > 0 && (
                <Reveal delay={0.12}>
                  <div>
                    <h2 className="font-mono text-[11px] tracking-[0.3em] text-brand-blue">ALTRI CONTENUTI</h2>
                    <div className="mt-4 flex gap-4 overflow-x-auto pb-2">
                      {p.gallery.map((g) => (
                        <div key={g.video} className="aspect-[9/16] w-36 shrink-0 overflow-hidden rounded-xl bg-navy">
                          <video className="h-full w-full object-cover" src={g.video} poster={g.poster} muted loop playsInline autoPlay preload="metadata" aria-label={`Altro contenuto del progetto ${p.client}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              )}
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="border border-slate-200 bg-brand-light/60 p-7 sm:p-9" data-testid="project-results">
                  <h2 className="font-mono text-[11px] tracking-[0.3em] text-brand-blue">I RISULTATI</h2>
                  <ul className="mt-5 space-y-5">
                    {p.risultati.map((r) => (
                      <li key={r.text} className="border-b border-navy/10 pb-5 last:border-0 last:pb-0">
                        <p className="font-display text-lg font-extrabold leading-snug tracking-tight text-navy">{r.text}</p>
                        <p className="mt-1.5 font-mono text-[10px] tracking-[0.2em] text-slate-500">{r.tag.toUpperCase()}</p>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 inline-block rounded-full bg-brand-gradient px-4 py-2 font-mono text-[10px] font-semibold tracking-[0.15em] text-white">
                    {p.attribution}
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-navy bg-grid-dark py-20 text-white lg:py-24">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 sm:px-8 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="max-w-2xl font-display text-3xl font-black leading-[1.02] tracking-tight sm:text-5xl">
              IL PROSSIMO PROGETTO POTREBBE ESSERE <span className="text-brand-gradient">IL TUO.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <button
              type="button"
              data-testid="project-cta"
              onClick={() => navigate("/", { state: { scrollTo: "contatti" } })}
              className="group inline-flex items-center gap-3 rounded-full bg-brand-gradient px-7 py-4 font-display text-sm font-bold tracking-wide text-white shadow-[0_12px_36px_-10px_rgba(37,99,235,0.5)] transition-all duration-300 hover:-translate-y-0.5"
            >
              RICHIEDI LA TUA CONSULENZA GRATUITA
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
};
